<template>
  <!-- Error notification -->
  <div v-if="messageError" class="notification is-danger actionNotification">
    <button class="delete" @click="handleRemoveError"></button>
    {{ messageError }}
  </div>
  
  <!-- Success notification -->
  <div v-if="messageSuccess" class="notification is-success actionNotification">
    <button class="delete" @click="handleRemoveSuccess"></button>
    {{ messageSuccess }}
  </div>
  
  <!-- Loading indicator -->
  <div v-if="loading" class="container is-fluid" style="padding: 2rem; text-align: center;">
    <div class="loader" style="margin: 2rem auto;"></div>
    <p>Chargement...</p>
  </div>
  
  <form v-else data-component="Lot/NewPreChiffrage" class="container is-fluid" @submit.prevent="handleSubmit">
    <header class="page-header">
      <h2 class="page-title">Nouveau pré-chiffrage</h2>
      <CloseButton :to="`/dossier/${propertyId}/timeline`" :doBack="false" />
    </header>

    <hr class="is-short-line" />

    <!-- Barre d'actions principale -->
    <div class="action-bar">
      <div class="action-bar-left">
        <button
          type="button"
          class="add-btn add-btn-cat"
          @click="addCATLine"
          title="Ajouter une ligne CAT"
        >
          <span class="add-btn-icon"><i class="fas fa-plus"></i></span>
          <span class="add-btn-label">Ajouter CAT</span>
        </button>

        <button
          type="button"
          class="add-btn add-btn-gpr"
          @click="addGPRLine"
          title="Ajouter une ligne GPR"
        >
          <span class="add-btn-icon"><i class="fas fa-plus"></i></span>
          <span class="add-btn-label">Ajouter GPR</span>
        </button>
      </div>

      <div class="action-bar-right">
        <div v-if="saving" class="saving-inline">
          <div class="spinner-small"></div>
          <span>{{ savingMessage }}</span>
        </div>
        <button
          v-else
          type="submit"
          class="submit-btn"
          :class="{ 'is-invalid': !isValid }"
          :disabled="!isValid || sending"
        >
          <i class="fas fa-check"></i>
          <span>Valider le pré-chiffrage</span>
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="listing">

      <!-- Section CAT -->
      <section class="form-section">
        <div class="section-header">
          <h3 class="section-title">
            <span class="section-badge section-badge-cat">CAT</span>
            Lignes de pré-chiffrage
          </h3>
          <span class="section-count" v-if="formData.lines.length > 0">
            {{ formData.lines.length }} ligne{{ formData.lines.length > 1 ? 's' : '' }}
          </span>
        </div>

        <FormCAT
          ref="formCATRef"
          v-model="formData.lines"
          :roomTypes="roomTypes"
          :workTypes="workTypes"
          :workFieldsByType="workFieldsByType"
        />
      </section>

      <!-- Section GPR -->
      <section class="form-section">
        <div class="section-header">
          <h3 class="section-title">
            <span class="section-badge section-badge-gpr">GPR</span>
            Lignes de pré-chiffrage - <small><i>A ne saisir que pour les logements propriété d'EDF</i></small>
          </h3>
          <span class="section-count" v-if="formData.gprLines.length > 0">
            {{ formData.gprLines.length }} ligne{{ formData.gprLines.length > 1 ? 's' : '' }}
          </span>
        </div>

        <FormGPR
          ref="formGPRRef"
          v-model="formData.gprLines"
        />
      </section>

      <!-- Section champs généraux -->
      <section class="form-section">
        <div class="section-header">
          <h3 class="section-title">
            <span class="section-badge section-badge-general">
              <i class="fas fa-info-circle"></i>
            </span>
            Informations complémentaires
          </h3>
        </div>
        <FormGeneralFields v-model="formData.generalFields" />
      </section>

      <!-- Erreur de validation -->
      <div v-if="!isValid && formData.lines.length > 0" class="error-information">
        <i class="fas fa-exclamation-circle"></i>
        Veuillez remplir tous les champs obligatoires avant de valider.
      </div>

      <!-- Bouton de validation en bas (duplication pour confort scroll) -->
      <div v-if="!saving" class="submit-footer">
        <button
          type="submit"
          class="submit-btn submit-btn-large"
          :class="{ 'is-invalid': !isValid }"
          :disabled="!isValid || sending"
        >
          <i class="fas fa-check"></i>
          <span v-if="!sending">Valider le pré-chiffrage</span>
          <span v-else>Enregistrement en cours...</span>
        </button>
      </div>

      <div v-if="saving" class="saving-status">
        <div class="spinner-container">
          <div class="spinner"></div>
          <p class="saving-text">{{ savingMessage }}</p>
        </div>
      </div>

    </div>
  </form>
</template>

<script>
import CloseButton from '@/components/Widget/CloseButton.vue'
import FormGPR from '@/components/PreChiffrage/FormGPR.vue'
import FormCAT from '@/components/PreChiffrage/FormCAT.vue'
import FormGeneralFields from '@/components/PreChiffrage/FormGeneralFields.vue'
import mutationService from '@/services/mutationService'
import { 
  getRoomTypeDictionary, 
  getWorkTypeDictionary, 
  getFieldsByWorkType
} from '@/utils/preChiffrageHelpers'

export default {
  name: 'NewPreChiffrage',
  
  components: {
    CloseButton,
    FormGPR,
    FormCAT,
    FormGeneralFields
  },
  
  data() {
    return {
      loading: true,
      sending: false,
      saving: false,
      savingMessage: '',
      messageError: '',
      messageSuccess: '',
      roomTypes: {},
      workTypes: {},
      workFieldsByType: {},
      formData: {
        lines: [],
        gprLines: [],
        generalFields: {
          commentairesGeneraux: '',
          chiffrageTermine: '',
          photosEnregistrees: ''
        }
      }
    }
  },
  
  computed: {
    propertyId() {
      return this.$route.params.id
    },
    
    isValid() {
      // Au moins une ligne CAT doit être présente
      if (this.formData.lines.length === 0) return false
      
      // Vérifier que chaque ligne CAT a les champs requis
      return this.formData.lines.every(line => {
        if (!line.roomType) return false
        if (this.shouldShowWorkType(line.roomType)) {
          if (!line.workType) return false
          const fields = this.workFieldsByType[line.workType]
          if (fields) {
            for (const [fieldKey, fieldData] of Object.entries(fields)) {
              if (fieldData.required && !line.workFields[fieldKey]) {
                return false
              }
            }
          }
        }
        return true
      })
    }
  },
  
  methods: {
    shouldShowWorkType(roomType) {
      return !['diag-electrique', 'daat'].includes(roomType)
    },
    
    handleRemoveError() {
      this.messageError = ''
    },
    
    handleRemoveSuccess() {
      this.messageSuccess = ''
    },

    /** Délègue l'ouverture de la modale CAT au composant enfant */
    addCATLine() {
      this.$refs.formCATRef?.addLine()
    },

    /** Délègue l'ouverture de la modale GPR au composant enfant */
    addGPRLine() {
      this.$refs.formGPRRef?.addLine()
    },
    
    async loadDictionaries() {
      try {
        const [roomTypesData, workTypesData] = await Promise.all([
          getRoomTypeDictionary(),
          getWorkTypeDictionary()
        ])
        this.roomTypes = roomTypesData
        this.workTypes = workTypesData
        await this.loadWorkFieldsByType()
      } catch (error) {
        console.error('Erreur lors du chargement des dictionnaires:', error)
        this.messageError = 'Erreur lors du chargement des dictionnaires'
      }
    },
    
    async loadWorkFieldsByType() {
      const workTypeKeys = Object.keys(this.workTypes)
      for (const workTypeKey of workTypeKeys) {
        try {
          const fields = await getFieldsByWorkType(workTypeKey)
          this.workFieldsByType[workTypeKey] = fields
        } catch (error) {
          console.error(`Erreur lors du chargement des champs pour ${workTypeKey}:`, error)
          this.workFieldsByType[workTypeKey] = {}
        }
      }
    },
    
    async handleSubmit() {
      if (!this.isValid) return
      
      this.sending = true
      this.saving = true
      this.messageError = ''
      this.messageSuccess = ''
      this.savingMessage = 'Préparation des données...'
      
      try {
        const preChiffrageData = {
          propertyId: this.propertyId,
          lines: this.formData.lines,
          gprLines: this.formData.gprLines,
          generalFields: this.formData.generalFields
        }
        
        this.savingMessage = 'Vérification de la connexion...'
        
        const result = await mutationService.createPreChiffrage(
          preChiffrageData,
          this.propertyId
        )
        
        if (result.success) {
          if (result.synced) {
            this.savingMessage = 'Sauvegarde en ligne réussie ✓'
            this.messageSuccess = '✓ Pré-chiffrage enregistré et synchronisé avec le serveur avec succès'
          } else {
            this.savingMessage = 'Sauvegarde locale réussie ✓'
            this.messageSuccess = '✓ Pré-chiffrage enregistré localement. Il sera automatiquement synchronisé avec le serveur dès que la connexion sera rétablie.'
          }
          
          setTimeout(() => {
            this.saving = false
            this.$router.push(`/dossier/${this.propertyId}/timeline`)
          }, 2500)
        } else {
          this.saving = false
          this.messageError = '✗ ' + (result.error || 'Erreur lors de l\'enregistrement du pré-chiffrage')
        }
        
      } catch (error) {
        console.error('Erreur lors de la soumission:', error)
        this.saving = false
        this.messageError = '✗ Erreur lors de l\'enregistrement du pré-chiffrage: ' + error.message
      } finally {
        this.sending = false
      }
    }
  },
  
  async mounted() {
    await this.loadDictionaries()
    this.loading = false
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';
@import './NewVisit.scss';

[data-component="Lot/NewPreChiffrage"] {
  &.container.is-fluid {
    padding: 0 1.8rem;
    position: relative;
    margin: 0;
    margin-bottom: 2rem;
    overflow: hidden;
  }
}

/* ── En-tête ────────────────────────────────────────────────────────────────── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $size-7;
}

.page-title {
  font-size: 1.6rem;
  font-family: Arial Rounded MT Bold;
  color: $darkblue;
  font-weight: 300;
  margin: 0;
}

hr.is-short-line {
  height: 1px;
  background: $gray;
  margin: 0.5rem 0 0.75rem 0;
  padding: 0;
  border: none;
}

/* ── Barre d'actions ────────────────────────────────────────────────────────── */
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.action-bar-left {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.action-bar-right {
  display: flex;
  align-items: center;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.2px;
}

.add-btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.add-btn:hover .add-btn-icon {
  transform: rotate(90deg);
}

.add-btn-cat {
  background: linear-gradient(135deg, $lightblue 0%, $lightblue2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(104, 207, 223, 0.35);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(104, 207, 223, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
}

.add-btn-gpr {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}

/* ── Bouton de validation ──────────────────────────────────────────────────── */
.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);

  &:hover:not([disabled]) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  }

  &[disabled],
  &.is-invalid {
    background: linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
}

.saving-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e40af;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e0e0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* ── Contenu / Sections ──────────────────────────────────────────────────────── */
.listing {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-section {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 1rem 1.1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #f3f4f6;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: $darkblue;
  margin: 0;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.section-badge-cat {
  background: #b2ebf2;
  color: #006064;
}

.section-badge-gpr {
  background: #dbeafe;
  color: #1e40af;
}

.section-badge-general {
  background: #f3f4f6;
  color: #6b7280;
  font-size: 0.8rem;
}

.optional-tag {
  font-size: 0.72rem;
  font-weight: 500;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  letter-spacing: 0.2px;
}

.section-count {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
}

/* ── Erreur de validation ──────────────────────────────────────────────────── */
.error-information {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fee2e2;
  border-left: 3px solid #dc2626;
  border-radius: 6px;
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 600;
}

/* ── Submit footer ────────────────────────────────────────────────────────────── */
.submit-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.5rem;
}

.submit-btn-large {
  padding: 0.75rem 2rem;
  font-size: 1rem;
}

/* ── Saving status ──────────────────────────────────────────────────────────── */
.saving-status {
  margin: 1rem 0;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 44px;
  height: 44px;
  border: 3px solid #e0e0e0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.saving-text {
  font-size: 1rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0;
  text-align: center;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* ── Notifications ──────────────────────────────────────────────────────────── */
.actionNotification {
  position: fixed;
  top: 20px;
  right: 20px;
  max-width: 500px;
  z-index: 9999;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 0.95rem;
  line-height: 1.5;
  border-radius: 8px;
  padding: 0.85rem 1rem;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.notification.is-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
  border-left: 4px solid #28a745;
}

.notification.is-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
  border-left: 4px solid #dc3545;
}
</style>
